/* eslint-disable react-hooks/exhaustive-deps */
import { gql, useLazyQuery } from '@apollo/client';
import { Badge, Box, Button, Divider, Flex, SimpleGrid, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { faFileAlt, faUserEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UserDislayNameById from '../../../../components/UserDislayNameById';
import c from '../../../../constant';
import SpreadSheetTableMtpl from '../../../../containers/MarketDevelopment/Components/mtpl-table';
import useMoment from '../../../../hooks/useMoment';
import useStore from '../../../../store';
import { toFormatCurrency } from '../../../../utils/format';
import { isAbleAllowEditing, isDataViewer } from '../../../../utils/market-development';
import { mdMonthlyDueDate } from '../../../../utils/mdReportDueDate';
import CompanyName from '../../../CompanyName';
import LabelDate from '../../../LabelDate';
import Loading from '../../../Loading';
import EditMonthlyReportModal from '../../../Modal/EditMonthlyReportModal';
import { CompanyBadge } from '../../../Other';
import EditHistoryModal from '../../Miscellaneous/edit-history-modal';
import SpreadSheetKeyValuePair from '../../Miscellaneous/spreadsheet-key-value-pair';
import ExcelGeneral from '../Excel/ExcelGeneral';
import SpreadSheetTable from './table';

const Q_DETAIL = gql`
	query get($id: String!) {
		getMonthlyClaimReportById(id: $id) {
			id
			reportType
			month
			year
			createdAt
			updatedAt
			company {
				id
				name
				nameKhmer
				logo
				type
			}
			isEditing
			editingUpdatedAt
			editingUpdatedBy
			editingDueDate
			editingDescription
			penalties {
				id
				active
			}
			claimItems {
				claimProducts {
					productLine {
						id
						nameKh
						nameEn
						type
					}
					productValues {
						id
						claimType
						claimValueType
						value
					}
				}
			}
			mtplItems {
				id
				reportId
				template {
					id
					titleKhmer
					titleEnglish
					code
				}
				labels {
					id
					templateLabel {
						id
						titleKhmer
						titleEnglish
						code
					}
					values {
						id
						value
						province {
							id
							name
							latinName
							code
						}
					}
				}
			}
		}
	}
`;

// mtpl
const Q_TEMPLATES = gql`
	query get($code: String!) {
		getTemplateByCode(code: $code) {
			id
			titleKhmer
			titleEnglish
			code
			order
			labels {
				id
				code
				titleKhmer
				titleEnglish
				level
				order
				readOnly
			}
			provinces {
				id
				code
				name
				latinName
			}
		}
	}
`;

const Page = forwardRef(({ id = null }, ref) => {
	const toast = useToast();
	const { t } = useTranslation();
	const { currentLang: lang } = useStore((state) => state.currentLang);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { toDateString, toMonth, toYear } = useMoment();
	const { currentUser: user } = useStore((state) => state?.currentUser);
	const [month, setMonth] = useState(null);
	const [year, setYear] = useState(null);
	const [isEditing, setEditing] = useState(false);
	const [createdAt, setCreatedAt] = useState(null);
	const [updatedAt, setUpdatedAt] = useState(null);
	const [isPenalized, setPenalized] = useState(false);
	const [company, setCompany] = useState(null);
	const [claimItem, setClaimItem] = useState(null);
	const [object, setObject] = useState(null);
	const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
	const [getDetail, { data: dataGet, loading: loadingGet, error: errorGet }] = useLazyQuery(Q_DETAIL);
	const [getTemplates, { data: dataTemplate, error: errorTemplate }] = useLazyQuery(Q_TEMPLATES);

	useEffect(loadDetail, [id]);

	useEffect(loadTemplates, [dataGet]);

	useEffect(() => {
		if (dataGet?.getMonthlyClaimReportById) {
			setMonth(parseInt(dataGet?.getMonthlyClaimReportById?.month));
			setYear(parseInt(dataGet?.getMonthlyClaimReportById?.year));
			setEditing(dataGet?.getMonthlyClaimReportById?.isEditing);
			setCreatedAt(dataGet?.getMonthlyClaimReportById?.createdAt);
			setUpdatedAt(dataGet?.getMonthlyClaimReportById?.updatedAt);
			setPenalized(dataGet?.getMonthlyClaimReportById?.penalties?.length > 0);
			setCompany(dataGet?.getMonthlyClaimReportById?.company);
			setClaimItem(dataGet?.getMonthlyClaimReportById?.claimItems[0]);
		}
	}, [dataGet]);

	useEffect(() => {
		if (errorGet) {
			if (errorGet?.graphQLErrors?.length > 0) {
				errorGet?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorGet?.message ? errorGet?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorGet]);

	// mtpl
	useEffect(() => {
		if (errorTemplate) {
			if (errorTemplate?.graphQLErrors?.length > 0) {
				errorTemplate?.graphQLErrors.map(({ message }, i) =>
					toast({
						title: t(message),
						status: 'error',
						isClosable: true,
						position: 'bottom-right',
					}),
				);
			} else {
				toast({
					title: t(errorTemplate?.message ? errorTemplate?.message : 'something went wrong'),
					status: 'error',
					isClosable: true,
					position: 'bottom-right',
				});
			}
		}
	}, [errorTemplate]);

	// mtpl
	useEffect(() => {
		if (dataTemplate?.getTemplateByCode && dataGet?.getMonthlyClaimReportById) {
			const template = dataTemplate?.getTemplateByCode;
			const labels = template?.labels || [];

			const provinces = (template?.provinces || []).filter((el) => el?.code !== '9999').sort((a, b) => String(a?.code).localeCompare(String(b?.code)));

			const localTemplate = dataGet?.getMonthlyClaimReportById?.mtplItems?.find((el) => el?.template?.code === c.mtplGClaim);
			const localLabels = localTemplate?.labels || [];

			const cells = labels?.map((label) => {
				if (label?.readOnly) {
					return provinces?.map(() => ({ value: '', readOnly: true }));
				}

				const localLabel = localLabels?.find((el) => el?.templateLabel?.code === label?.code);
				const localValues = localLabel?.values || [];

				return provinces.map((p) => {
					const found = localValues?.find((v) => v?.province?.code === p?.code);
					const raw = found?.value;

					return {
						value: toFormatCurrency(raw, null) ?? '',
						readOnly: true,
					};
				});
			});

			setObject({
				...template,
				provinces,
				cells,
			});
		}
	}, [dataTemplate, dataGet]);

	function loadDetail() {
		id && getDetail({ variables: { id } });
	}

	// mtpl
	function loadTemplates() {
		dataGet?.getMonthlyClaimReportById && getTemplates({ variables: { code: c.mtplGClaim } });
	}

	const dueDate = useMemo(() => mdMonthlyDueDate({ year, month }), [year, month]);
	const isDueDateReached = useMemo(() => (dueDate ? moment().isSameOrAfter(dueDate, 'second') : false), [dueDate]);
	const canViewData = useMemo(() => isDataViewer({ user, companyType: 'General' }), [user]);
	const canAllowEditing = useMemo(() => canViewData && isAbleAllowEditing({ user, companyType: 'General' }), [user]);

	return (
		<>
			{loadingGet ? (
				<>
					<Loading />
				</>
			) : (
				<>
					<Flex pl={4} pr={4}>
						<SimpleGrid columns={[1, null, 2]} w="100%" spacingX={4} spacingY={4}>
							<Flex w="100%" direction="column" border="1px dotted #dbdbdb" borderRadius={c.borderRadius} pl={4} pr={4} pt={4} pb={4}>
								<>
									{isPenalized && (
										<Box>
											<Badge colorScheme="red" variant="solid">
												{t('Penalized')}
											</Badge>
										</Box>
									)}
								</>
								<SpreadSheetKeyValuePair label="Company" value={<CompanyName company={company} />} />
								<SpreadSheetKeyValuePair label="Type" value={<CompanyBadge type={company?.type} />} />
								<SpreadSheetKeyValuePair label="Monthly" value={toMonth(month)} />
								<SpreadSheetKeyValuePair label="Year" value={toYear(year)} />
								<SpreadSheetKeyValuePair label="Submitted At" value={<LabelDate input={createdAt} outputFormat={c.dateTimeFormat} />} />
								<>
									{updatedAt && (
										<>
											<SpreadSheetKeyValuePair
												label="Updated at"
												value={<LabelDate input={updatedAt} outputFormat={c.dateTimeFormat} />}
											/>
										</>
									)}
								</>
								{isDueDateReached && canViewData && isEditing && (
									<>
										<Divider variant="dashed" mt={2} mb={2} />
										<SpreadSheetKeyValuePair
											label="Allow Editing"
											value={<LabelDate input={dataGet?.getMonthlyClaimReportById?.editingUpdatedAt} outputFormat={c.dateTimeFormat} />}
										/>
										<SpreadSheetKeyValuePair
											label="By"
											value={<UserDislayNameById id={dataGet?.getMonthlyClaimReportById?.editingUpdatedBy} />}
										/>
										<SpreadSheetKeyValuePair
											label="Due Date"
											value={<LabelDate input={dataGet?.getMonthlyClaimReportById?.editingDueDate} outputFormat={c.dateTimeFormat} />}
										/>
										<SpreadSheetKeyValuePair label="Description" value={dataGet?.getMonthlyClaimReportById?.editingDescription} />
										{canAllowEditing && (
											<>
												<Box mt={2}>
													<Button
														onMouseDown={onOpen}
														isLoading={false}
														isDisabled={false}
														onClick={() => null}
														leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
														colorScheme="red"
														variant="solid"
														size="sm"
														borderRadius={c.borderRadius}
														border="1px solid #bdc3c7"
														mr={2}
														width={lang === 'kh' ? '219px' : '195px'}
													>
														{t('Stop Allowing Editing')}
													</Button>
												</Box>
											</>
										)}
									</>
								)}
								<Flex mt={2}>
									{isDueDateReached && canAllowEditing && !isEditing && (
										<Button
											onMouseDown={onOpen}
											isLoading={false}
											isDisabled={false}
											onClick={() => null}
											leftIcon={<FontAwesomeIcon icon={faUserEdit} style={{ fontSize: 16 }} />}
											colorScheme="gray"
											variant="solid"
											size="sm"
											borderRadius={c.borderRadius}
											border="1px solid #bdc3c7"
											mr={2}
										>
											{t('Allow Editing')}
										</Button>
									)}
									{claimItem && object && canViewData && (
										<ExcelGeneral month={month} year={year} numColumns={10} claimItem={claimItem} mtplItems={object} />
									)}
									{canViewData && (
										<Flex ml={2}>
											<Button
												colorScheme="gray"
												variant="solid"
												size="sm"
												borderRadius={c.borderRadius}
												border="1px solid #bdc3c7"
												leftIcon={<FontAwesomeIcon icon={faFileAlt} style={{ fontSize: 16 }} />}
												onClick={onHistoryOpen}
											>
												{t('View Logs')}
											</Button>
										</Flex>
									)}
								</Flex>
								{!isDueDateReached && canViewData && dueDate && (
									<Box h="100%" mt={2}>
										<Text fontSize="15px" fontStyle="italic" fontWeight="bold" justifyContent="center" color="orange.500">
											{t('Report can still be edited until the due date:')}&nbsp;
											{toDateString(dueDate, c.dateTimeFormat)}
										</Text>
									</Box>
								)}
							</Flex>
						</SimpleGrid>
					</Flex>
					<Box pl={4} pr={4} pb={4} pt={4} overflow="clip" hidden={canViewData ? false : true}>
						<Tabs>
							<TabList borderBottomWidth="1px" borderBottomStyle="solid" borderBottomColor="gray.200">
								{claimItem && (
									<Tab>
										<Text>{t('Claim')}</Text>
									</Tab>
								)}
								{object && (
									<Tab>
										<Text>{t('MTPL')}</Text>
									</Tab>
								)}
							</TabList>
							<TabPanels>
								{claimItem && (
									<TabPanel pl={0} pr={0} pb={0}>
										<SpreadSheetTable claimProducts={claimItem?.claimProducts} />
									</TabPanel>
								)}
								{object && (
									<TabPanel pl={0} pr={0} pb={0}>
										<SpreadSheetTableMtpl
											labels={object?.labels ?? []}
											provinces={object?.provinces ?? []}
											cells={object?.cells ?? []}
											onChange={() => null}
										/>
									</TabPanel>
								)}
							</TabPanels>
						</Tabs>
					</Box>
				</>
			)}
			{isOpen && <EditMonthlyReportModal id={id} type="Claim" isEditable={isEditing} onClose={onClose} onCallback={loadDetail} />}
			{isHistoryOpen && <EditHistoryModal id={id} period="Monthly" category="Claim" isOpen={isHistoryOpen} onClose={onHistoryClose} />}
		</>
	);
});

export default Page;
